import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { clientNoLoader } from "api/reduxLoaderApi";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { createWidget, editWidget } from "../slice/widgetThunks";
import {
  BACKFILL_SERVICES_BASE,
  moduleOptions,
  visualizationOptions,
} from "../widgetConstants";
import {
  FiltersEditor,
  filtersToForm,
  formToFilters,
  validateFilters,
} from "./FiltersEditor";
import {
  VisualizationOptionsEditor,
  formToOptions,
  optionsToForm,
  retypeOptions,
  validateOptions,
} from "./VisualizationOptionsEditor";
import { WidgetImageField, getImageId } from "./WidgetImage";

const emptyForm = {
  name: "",
  description: "",
  module: "",
  visualization_type: "",
  visualization_options: optionsToForm(),
  backfill_service: "",
  default_height: "",
  default_width: "",
  permission_required: false,
  is_active: true,
  available_filters: [],
  image: "",
};

const toNumber = (size) => (size ? String(parseFloat(size) || "") : "");
const toPx = (value) => `${value}px`;

const widgetToForm = (widget) => ({
  name: widget.name || "",
  description: widget.description || "",
  module: widget.module || "",
  visualization_type: widget.visualization_type || "",
  visualization_options: optionsToForm(
    widget.visualization_options,
    widget.visualization_type
  ),
  backfill_service:
    widget.backfill_service?.id || widget.backfill_service || "",
  default_height: toNumber(widget.default_height),
  default_width: toNumber(widget.default_width),
  permission_required: !!widget.permission_required,
  is_active: widget.is_active !== false,
  available_filters: filtersToForm(widget.available_filters),
  image: getImageId(widget.image),
});

const formToBody = (form) => {
  const body = {
    ...form,
    name: form.name.trim(),
    description: form.description.trim(),
    default_height: toPx(form.default_height),
    default_width: toPx(form.default_width),
    available_filters: formToFilters(form.available_filters),
    visualization_options: formToOptions(
      form.visualization_options,
      form.visualization_type
    ),
    backfill_service: form.backfill_service || null,
    image: form.image || null,
  };
  if (!body.module) delete body.module;
  // The backend rejects an empty description on create; PATCH clears it
  // with "" (via diffBody).
  if (!body.description) delete body.description;
  // Create rejects nulls; PATCH sends them (via diffBody) to clear the field.
  if (!body.visualization_options) delete body.visualization_options;
  if (!body.backfill_service) delete body.backfill_service;
  if (!body.image) delete body.image;
  // Sent only when filters exist, so widgets without them still save on
  // backends that don't know the field yet.
  if (!body.available_filters.length) delete body.available_filters;
  return body;
};

const validate = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.visualization_type)
    errors.visualization_type = "Visualization type is required";
  if (!(Number(form.default_height) > 0))
    errors.default_height = "Enter a positive number";
  if (!(Number(form.default_width) > 0))
    errors.default_width = "Enter a positive number";
  const filterErrors = validateFilters(form.available_filters);
  if (Object.keys(filterErrors).length) errors.available_filters = filterErrors;
  const optionErrors = validateOptions(
    form.visualization_options,
    form.visualization_type
  );
  if (Object.keys(optionErrors).length)
    errors.visualization_options = optionErrors;
  return errors;
};

const isSameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// PATCH must contain only changed fields and must not be empty
const diffBody = (original, body) => {
  const changes = Object.keys(body).reduce(
    (acc, key) =>
      !isSameValue(body[key], original[key])
        ? { ...acc, [key]: body[key] }
        : acc,
    {}
  );
  if (original.description && !body.description) changes.description = "";
  // All filters removed: send [] explicitly so the stored list is cleared.
  if (original.available_filters && !body.available_filters)
    changes.available_filters = [];
  if (original.visualization_options && !body.visualization_options)
    changes.visualization_options = null;
  if (original.backfill_service && !body.backfill_service)
    changes.backfill_service = null;
  if (original.image && !body.image) changes.image = null;
  return changes;
};

export const WidgetModal = ({ open, widget, handleClose, onSaved }) => {
  const dispatch = useDispatch();
  const isEdit = !!widget?.id;
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [services, setServices] = useState([]);

  // The service catalog is superadmin-only, as is this page.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    clientNoLoader("get")(`${BACKFILL_SERVICES_BASE}?limit=all`)({})({}).then(
      (res) => {
        if (active && !res?.error && Array.isArray(res.message))
          setServices(res.message);
      }
    );
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setForm(isEdit ? widgetToForm(widget) : emptyForm);
    setErrors({});
  }, [open, widget, isEdit]);

  const handleChangeField = (name) => ({ target }) => {
    const value = target.type === "checkbox" ? target.checked : target.value;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors(({ [name]: removed, ...rest }) => rest);
  };

  const onFinish = () => {
    handleClose();
    onSaved();
  };

  const handleSave = () => {
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const body = formToBody(form);
    if (!isEdit) return dispatch(createWidget(body, onFinish));

    // The form fills option defaults; compare against what is stored so a
    // widget without saved options still gets them on its first save.
    const original = {
      ...formToBody(widgetToForm(widget)),
      visualization_options: widget.visualization_options || undefined,
    };
    const changes = diffBody(original, body);
    if (!Object.keys(changes).length) return handleClose();
    return dispatch(editWidget(widget.id, changes, onFinish));
  };

  const fieldProps = (name) => ({
    value: form[name],
    onChange: handleChangeField(name),
    error: !!errors[name],
    helperText: errors[name],
    variant: "outlined",
    size: "small",
    fullWidth: true,
  });

  // The catalog DTO omits name; the widget's populated service carries it.
  const serviceOptions = services.map((service) =>
    service.id === widget?.backfill_service?.id
      ? { ...widget.backfill_service, ...service }
      : service
  );
  const selectedService = serviceOptions.find(
    (service) => service.id === form.backfill_service
  );

  const pxAdornment = {
    endAdornment: <InputAdornment position="end">px</InputAdornment>,
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>{isEdit ? "Edit Widget" : "Add Widget"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <WidgetImageField
              value={form.image}
              onChange={(image) => setForm((prev) => ({ ...prev, image }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Name *" {...fieldProps("name")} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              multiline
              rows={3}
              {...fieldProps("description")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField select label="Module" {...fieldProps("module")}>
              {/* backend may not accept clearing a stored module on PATCH */}
              {(!isEdit || !widget.module) && (
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
              )}
              {moduleOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Backfill Service"
              {...fieldProps("backfill_service")}
              helperText={
                selectedService?.data_source?.end_point
                  ? `${selectedService.data_source.method || "GET"} ${
                      selectedService.data_source.end_point
                    }`
                  : "Provides the widget data and its base filters"
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {serviceOptions.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name || service.key}
                </MenuItem>
              ))}
            </TextField>
            {!!selectedService?.filters?.length && (
              <Typography variant="caption" color="textSecondary">
                Service filters:{" "}
                {selectedService.filters
                  .map((filter) => filter.label || filter.key)
                  .join(", ")}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Visualization Type *"
              {...fieldProps("visualization_type")}
              onChange={(e) => {
                const visualization_type = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  visualization_type,
                  visualization_options: retypeOptions(
                    prev.visualization_options,
                    visualization_type
                  ),
                }));
                setErrors(({ visualization_type: removed, ...rest }) => rest);
              }}
            >
              {visualizationOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              type="number"
              label="Default Height *"
              inputProps={{ min: 1 }}
              InputProps={pxAdornment}
              {...fieldProps("default_height")}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              type="number"
              label="Default Width *"
              inputProps={{ min: 1 }}
              InputProps={pxAdornment}
              {...fieldProps("default_width")}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={form.permission_required}
                  onChange={handleChangeField("permission_required")}
                />
              }
              label="Permission Required"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={form.is_active}
                  onChange={handleChangeField("is_active")}
                />
              }
              label="Active"
            />
          </Grid>
          <Grid item xs={12}>
            <VisualizationOptionsEditor
              visualizationType={form.visualization_type}
              value={form.visualization_options}
              errors={errors.visualization_options}
              onChange={(visualization_options) => {
                setForm((prev) => ({ ...prev, visualization_options }));
                setErrors(
                  ({ visualization_options: removed, ...rest }) => rest
                );
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FiltersEditor
              value={form.available_filters}
              errors={errors.available_filters}
              onChange={(available_filters) => {
                setForm((prev) => ({ ...prev, available_filters }));
                setErrors(({ available_filters: removed, ...rest }) => rest);
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="default">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
